import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { closeProfile, setLogout } from "features/authSlice";
import { useLoanSelectionRouter } from "hooks/useLoanSelectionRouter";
import { useLoanSidebarData } from "hooks/useLoanSidebarData";
import { getLoanTypeIcon } from "utilities/loan-sidebar/getLoanTypeIcon";
import { deleteAllFiles } from "utilities/files";
import LoanCentricSidebarNavView from "./LoanCentricSidebarNavView";

export interface LoanCentricSidebarNavProps {
  readonly onLoanClick?: (loanId: string) => void;
  readonly setIsNavOpen?: (open: boolean) => void;
}

export default function LoanCentricSidebarNav({ onLoanClick, setIsNavOpen }: LoanCentricSidebarNavProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loans, isLoading } = useLoanSidebarData();
  const routeLoanSelection = useLoanSelectionRouter();

  const handleLoanClick = useCallback(
    (loanId: string) => {
      if (onLoanClick) {
        onLoanClick(loanId);
        return;
      }

      const row = loans.find((loan) => loan.id === loanId);
      if (!row || row.isActive) {
        return;
      }

      routeLoanSelection({
        loan: row.loan,
        losType: row.losType,
        loanKey: row.loanKey,
        source: "sidebar",
      });
    },
    [loans, onLoanClick, routeLoanSelection],
  );

  const loanItems = useMemo(
    () =>
      loans.map((loan) => ({
        id: loan.id,
        label: loan.label,
        baseState: loan.baseState,
        isActive: loan.isActive,
        icon: getLoanTypeIcon(),
      })),
    [loans],
  );

  const profileItems = useMemo(
    () => [
      {
        id: "settings",
        label: "Settings",
        onClick: () => {
          dispatch(closeProfile());
          navigate("/settings");
        },
      },
      {
        id: "logout",
        label: "Logout",
        onClick: async () => {
          await deleteAllFiles();
          dispatch(setLogout());
          navigate("/");
        },
      },
    ],
    [dispatch, navigate],
  );

  return (
    <LoanCentricSidebarNavView
      loans={loanItems}
      profileItems={profileItems}
      isLoading={isLoading}
      onLoanClick={handleLoanClick}
    />
  );
}
